import { useEffect, useState } from "react"
import { Box, MenuItem, Button } from "@mui/material"
import { StyledTextField } from "components/inputs/StyledTextField"
import { SubmitHandler, useForm, Controller } from "react-hook-form"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import "dayjs/locale/en-gb"
import { defaultFilterValuesInvoices } from "layouts/invoices/utils/const"
import { DateRangePopup } from "layouts/sessions/filter/DateRangePopup"

export interface IFormInvoiceInput {
    from: number
    until: number
    number: string
    status: string
}

interface FilterProps {
    onSubmit: SubmitHandler<IFormInvoiceInput>
}

export const InvoicesFilter = ({ onSubmit }: FilterProps) => {
    const { control, handleSubmit, setValue } = useForm({
        defaultValues: defaultFilterValuesInvoices,
        mode: "onChange",
    })
    const [dateRange, setDateRange] = useState("")
    const [openDatePopup, setOpenDatePopup] = useState(false)
    const [fromDate, setFromDate] = useState<null | number>(null)
    const [untilDate, setUntilDate] = useState<null | number>(null)

    const labelDateRangeFilter = "Période de dates d'émission"

    useEffect(() => {
        let dateRange = ""
        if (fromDate && untilDate) {
            const fromDateObject = new Date(fromDate)
            const untilDateObject = new Date(untilDate)
            dateRange = `Du ${fromDateObject.toLocaleDateString("fr-FR")} au ${untilDateObject.toLocaleDateString("fr-FR")}`
        } else if (fromDate) {
            const date = new Date(fromDate)
            dateRange = "Depuis le " + date.toLocaleDateString("fr-FR")
        } else if (untilDate) {
            const date = new Date(untilDate)
            dateRange = "Jusqu'au " + date.toLocaleDateString("fr-FR")
        }
        setDateRange(dateRange)
    }, [untilDate, fromDate, setDateRange])

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <form onSubmit={handleSubmit(onSubmit)}>
                <DateRangePopup
                    open={openDatePopup}
                    title={labelDateRangeFilter}
                    control={control}
                    handleClose={() => setOpenDatePopup(false)}
                    setFromDate={setFromDate}
                    setUntilDate={setUntilDate}
                    fromDate={fromDate}
                    untilDate={untilDate}
                    setValue={setValue}
                />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "1rem",
                        width: "100%",
                        height: "56px",
                    }}
                >
                    <StyledTextField
                        data-testid="invoice-date-filter"
                        id="invoice-page-date"
                        label={labelDateRangeFilter}
                        slotProps={{
                            input: {
                                readOnly: true,
                                disableUnderline: true,
                            },
                        }}
                        value={dateRange}
                        onClick={() => {
                            setOpenDatePopup(true)
                        }}
                        focused={false}
                    />
                    <Controller
                        name="number"
                        control={control}
                        render={({ field }) => (
                            <StyledTextField
                                id="invoices-page-number"
                                label="Numéro"
                                {...field}
                            />
                        )}
                    />
                    <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                            <StyledTextField
                                id="invoices-page-status"
                                label="Status"
                                select
                                {...field}
                            >
                                <MenuItem value="Tous">Tous</MenuItem>
                                <MenuItem value="Payée">Payée</MenuItem>
                                <MenuItem value="Non payée">Impayée</MenuItem>
                                <MenuItem value="En attente de paiement">
                                    En attente de paiement
                                </MenuItem>
                            </StyledTextField>
                        )}
                    />
                    <Button
                        data-testid="submit-filter-invoices"
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{
                            minWidth: "56px",
                            height: "56px",
                            aspectRatio: "1/1",
                            borderRadius: "10px",
                            "&:hover, &:focus": {
                                backgroundColor: "primary.hover",
                            },
                        }}
                    >
                        <SearchOutlinedIcon
                            sx={{
                                color: "white",
                                fontSize: "2rem",
                            }}
                        />
                    </Button>
                </Box>
            </form>
        </LocalizationProvider>
    )
}
